import { memo, useMemo } from "react"
import { cn } from "@/shared/utils/general/cn"
import { parseDbDate } from "@totodo/operations"
import { getDeadlineText, isTodayOrPast } from "@/shared/utils/date/dateUtils"
import FlagIcon from "@/shared/icons/totodoicons/solid/flag.svg?react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaskFlagProps {
  /** Task.dueAt — stringified ms timestamp */
  dueAt?: string
  className?: string
  /** Compact mode — icon only, text as tiny badge overlay */
  compact?: boolean
}

// ---------------------------------------------------------------------------
// Inline SVG flag icon — matches v1 pikaicons flag.svg
// ---------------------------------------------------------------------------

function FlagSvg({ overdue }: { overdue: boolean }) {
  return (
    <FlagIcon
      width={18}
      height={18}
      className={overdue ? "text-destructive" : "text-muted-foreground"}
    />
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Flag icon + relative deadline text for tasks with a due date.
 *
 * v1 pattern: CardPreviewFlag.
 * - Red flag if overdue (today or past)
 * - Gray flag if future
 * - Text shows relative date ("Today", "Tmrw", "3d")
 */
export const TaskFlag = memo(function TaskFlag({
  dueAt,
  className,
  compact,
}: TaskFlagProps) {
  const info = useMemo(() => {
    if (!dueAt || parseDbDate(dueAt) === undefined) return null
    return {
      overdue: isTodayOrPast(dueAt),
      text: getDeadlineText(dueAt),
    }
  }, [dueAt])

  if (!info) return null

  if (compact) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-0.5 pl-1",
          "deadline-chip",
          className,
        )}
      >
        <FlagSvg overdue={info.overdue} />
        <span className="text-[9px] font-semibold leading-tight">
          {info.text}
        </span>
      </span>
    )
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 pl-1",
        "text-[11px]",
        info.overdue
          ? "text-destructive"
          : "text-muted-foreground/60",
        className,
      )}
    >
      <FlagSvg overdue={info.overdue} />
      {info.text}
    </span>
  )
})
